<template>
  <div class="space-y-6">

    <!-- Résumé de l'analyse -->
    <div class="bg-blue-50 border border-blue-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-blue-900">📋 Résumé de l'analyse</h4>
      <div v-if="analysisReport" class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span class="font-medium text-blue-700">Nouvelles sections:</span>
          <span class="ml-2 font-bold text-green-600">{{ sectionsToCreate }}</span>
        </div>
        <div>
          <span class="font-medium text-blue-700">Sections existantes:</span>
          <span class="ml-2 font-bold text-blue-900">{{ sectionsToUpdate }}</span>
        </div>
        <div v-if="similarSections.length > 0">
          <span class="font-medium text-blue-700">Sections similaires:</span>
          <span class="ml-2 font-bold text-orange-600">{{ similarSections.length }}</span>
        </div>
        <div v-if="newSectionsWithSimilarity.length > 0">
          <span class="font-medium text-orange-700">Sections avec similarité détectée:</span>
          <span class="ml-2 font-bold text-orange-900">{{ newSectionsWithSimilarity.length }}</span>
        </div>
        <div v-if="translationConflicts.length > 0">
          <span class="font-medium text-blue-700">Conflits de traduction:</span>
          <span class="ml-2 font-bold text-yellow-600">{{ translationConflicts.length }}</span>
        </div>
        <div v-if="removedSections.length > 0">
          <span class="font-medium text-blue-700">Sections à supprimer:</span>
          <span class="ml-2 font-bold text-red-700">{{ sectionsToRemove }}</span>
        </div>
      </div>
    </div>

    <!-- Conflits de propriétés des sections (uniquement 2 choix: utiliser existant ou importé) -->
    <div v-if="detailedConflicts && detailedConflicts.length > 0"
      class="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-yellow-800">⚠️ Conflits de propriétés détectés</h4>
      <div class="space-y-4">
        <div v-for="conflict in detailedConflicts" :key="conflict.id" class="bg-white p-4 rounded border">
          <div class="flex justify-between items-start mb-3">
            <div>
              <span class="font-medium text-gray-900">{{ conflict.section_id }}</span>
              <span class="text-sm text-gray-600 ml-2">({{ conflict.field_name }})</span>
            </div>
            <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              {{ getConflictTypeLabel(conflict.type) }}
            </span>
          </div>

          <p class="text-sm text-gray-700 mb-4">{{ conflict.description }}</p>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="bg-red-50 p-3 rounded">
              <span class="font-medium text-red-700 text-sm block mb-2">Valeur existante:</span>
              <div class="text-sm text-gray-900">{{ conflict.existing_value }}</div>
            </div>
            <div class="bg-green-50 p-3 rounded">
              <span class="font-medium text-green-700 text-sm block mb-2">Valeur importée:</span>
              <div class="text-sm text-gray-900">{{ conflict.imported_value }}</div>
            </div>
          </div>

          <div class="space-y-2">
            <span class="font-medium text-gray-700 text-sm">Choisir la valeur à utiliser:</span>
            <div class="flex space-x-3">
              <label class="flex items-center cursor-pointer">
                <input type="radio" :name="`conflict_${conflict.id}`" value="keep_existing"
                  :checked="(resolutions[conflict.id] || conflict.resolution) === 'keep_existing'"
                  class="mr-2 text-red-600" @change="updateResolution(conflict.id, 'keep_existing')" />
                <span class="text-sm">Utiliser la valeur existante</span>
              </label>
              <label class="flex items-center cursor-pointer">
                <input type="radio" :name="`conflict_${conflict.id}`" value="use_imported"
                  :checked="(resolutions[conflict.id] || conflict.resolution) === 'use_imported'"
                  class="mr-2 text-green-600" @change="updateResolution(conflict.id, 'use_imported')" />
                <span class="text-sm">Utiliser la valeur importée</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conflits de traductions avec boutons et affichage détaillé -->
    <div v-if="translationConflicts && translationConflicts.length > 0"
      class="bg-orange-50 border border-orange-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-orange-800">🌐 Conflits de traductions</h4>
      <div class="space-y-4">
        <div v-for="conflict in translationConflicts"
          :key="`${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`"
          class="bg-white p-4 rounded border">
          <div class="flex justify-between items-start mb-3">
            <div>
              <span class="font-medium text-gray-900">{{ conflict.section_name }}</span>
              <span class="text-sm text-gray-600 ml-2">({{ conflict.language_code.toUpperCase() }})</span>
            </div>
            <span class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
              {{ conflict.field_type || 'Tous champs' }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="bg-red-50 p-3 rounded">
              <span class="font-medium text-red-700 text-sm block mb-2">Traduction existante:</span>
              <div class="text-sm text-gray-900">
                <div v-if="conflict.field_type === 'titre'">
                  {{ conflict.existing_translation?.titre || '(Pas de traduction)' }}
                </div>
                <div v-else-if="conflict.field_type === 'description'">
                  {{ conflict.existing_translation?.description || '(Pas de traduction)' }}
                </div>
                <div v-else>
                  <div><strong>Titre:</strong> {{ conflict.existing_translation?.titre || '(Pas de traduction)' }}</div>
                  <div><strong>Description:</strong> {{ conflict.existing_translation?.description ||
                    '(Pas_de_traduction) ' }}</div>
                </div>
              </div>
            </div>
            <div class="bg-green-50 p-3 rounded">
              <span class="font-medium text-green-700 text-sm block mb-2">Traduction importée:</span>
              <div class="text-sm text-gray-900">
                <div v-if="conflict.field_type === 'titre'">
                  {{ conflict.imported_translation?.titre || '(Pas de traduction)' }}
                </div>
                <div v-else-if="conflict.field_type === 'description'">
                  {{ conflict.imported_translation?.description || '(Pas de traduction)' }}
                </div>
                <div v-else>
                  <div><strong>Titre:</strong> {{ conflict.imported_translation?.titre || '(Pas de traduction)' }}</div>
                  <div><strong>Description:</strong> {{ conflict.imported_translation?.description ||
                    '(Pas_de_traduction) ' }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <span class="font-medium text-gray-700 text-sm">Choisir l'action:</span>
            <div class="flex flex-col space-y-2">
              <label
                class="flex items-center cursor-pointer p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                <input type="radio"
                  :name="`translation_${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`"
                  value="create_schema_specific"
                  :checked="getTranslationResolution(conflict) === 'create_schema_specific'" class="mr-3 text-blue-600"
                  @change="updateTranslationResolution(conflict.section_name, conflict.language_code, conflict.field_type || 'all', 'create_schema_specific')" />
                <div>
                  <span class="text-sm font-medium">Créer une traduction schéma-spécifique</span>
                  <div class="text-xs text-gray-600 mt-1">Recommandé - N'affecte que ce schéma</div>
                </div>
              </label>

              <label v-if="conflict.existing_translation?.source === 'schema'"
                class="flex items-center cursor-pointer p-2 bg-green-50 rounded hover:bg-green-100 transition-colors">
                <input type="radio"
                  :name="`translation_${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`"
                  value="update_schema_translation"
                  :checked="getTranslationResolution(conflict) === 'update_schema_translation'"
                  class="mr-3 text-green-600"
                  @change="updateTranslationResolution(conflict.section_name, conflict.language_code, conflict.field_type || 'all', 'update_schema_translation')" />
                <div>
                  <span class="text-sm font-medium">Mettre à jour la traduction du schéma</span>
                  <div class="text-xs text-gray-600 mt-1">Remplace la traduction schéma existante</div>
                </div>
              </label>

              <label
                class="flex items-center cursor-pointer p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                <input type="radio"
                  :name="`translation_${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`"
                  value="use_global" :checked="getTranslationResolution(conflict) === 'use_global'"
                  class="mr-3 text-gray-600"
                  @change="updateTranslationResolution(conflict.section_name, conflict.language_code, conflict.field_type || 'all', 'use_global')" />
                <div>
                  <span class="text-sm font-medium">Utiliser la traduction globale</span>
                  <div class="text-xs text-gray-600 mt-1">Ignorer la traduction importée</div>
                </div>
              </label>

              <label class="flex items-center cursor-pointer p-2 bg-red-50 rounded hover:bg-red-100 transition-colors">
                <input type="radio"
                  :name="`translation_${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`"
                  value="overwrite_global" :checked="getTranslationResolution(conflict) === 'overwrite_global'"
                  class="mr-3 text-red-600"
                  @change="updateTranslationResolution(conflict.section_name, conflict.language_code, conflict.field_type || 'all', 'overwrite_global')" />
                <div>
                  <span class="text-sm font-medium">Écraser la traduction globale</span>
                  <div class="text-xs text-red-600 mt-1">⚠️ Affecte tous les schémas - supprime la traduction schéma
                    actuelle</div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Traductions des nouvelles sections -->
    <div v-if="newSectionTranslationConflicts && newSectionTranslationConflicts.length > 0"
      class="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-emerald-800">🆕 Traductions des nouvelles sections</h4>
      <div class="mb-3 text-sm text-emerald-700">
        Ces nouvelles sections contiennent des traductions. Choisissez comment les gérer.
      </div>
      <div class="space-y-4">
        <div v-for="conflict in newSectionTranslationConflicts"
          :key="`new_${conflict.section_name}_${conflict.language_code}`" class="bg-white p-4 rounded border">
          <div class="flex justify-between items-start mb-3">
            <div>
              <span class="font-medium text-gray-900">{{ conflict.section_name }}</span>
              <span class="text-sm text-gray-600 ml-2">({{ conflict.language_code.toUpperCase() }})</span>
            </div>
            <span class="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
              Nouvelle section
            </span>
          </div>

          <div class="bg-green-50 p-3 rounded mb-4">
            <span class="font-medium text-green-700 text-sm block mb-2">Traduction à importer:</span>
            <div class="text-sm text-gray-900">
              <div><strong>Titre:</strong> {{ conflict.imported_translation?.titre || '(Pas de traduction)' }}</div>
              <div><strong>Description:</strong> {{ conflict.imported_translation?.description || '(Pas de traduction)'
                }}</div>
            </div>
          </div>

          <div class="space-y-3">
            <span class="font-medium text-gray-700 text-sm">Choisir l'action:</span>
            <div class="flex flex-col space-y-2">
              <label
                class="flex items-center cursor-pointer p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                <input type="radio" :name="`new_translation_${conflict.section_name}_${conflict.language_code}`"
                  value="create_schema_specific"
                  :checked="getNewSectionTranslationResolution(conflict) === 'create_schema_specific'"
                  class="mr-3 text-blue-600"
                  @change="updateNewSectionTranslationResolution(conflict.section_name, conflict.language_code, 'create_schema_specific')" />
                <div>
                  <span class="text-sm font-medium">Créer une traduction schéma-spécifique</span>
                  <div class="text-xs text-gray-600 mt-1">Recommandé - N'affecte que ce schéma</div>
                </div>
              </label>

              <label
                class="flex items-center cursor-pointer p-2 bg-green-50 rounded hover:bg-green-100 transition-colors">
                <input type="radio" :name="`new_translation_${conflict.section_name}_${conflict.language_code}`"
                  value="create_global" :checked="getNewSectionTranslationResolution(conflict) === 'create_global'"
                  class="mr-3 text-green-600"
                  @change="updateNewSectionTranslationResolution(conflict.section_name, conflict.language_code, 'create_global')" />
                <div>
                  <span class="text-sm font-medium">Créer une traduction globale</span>
                  <div class="text-xs text-gray-600 mt-1">Disponible pour tous les schémas utilisant cette section</div>
                </div>
              </label>

              <label
                class="flex items-center cursor-pointer p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                <input type="radio" :name="`new_translation_${conflict.section_name}_${conflict.language_code}`"
                  value="ignore" :checked="getNewSectionTranslationResolution(conflict) === 'ignore'"
                  class="mr-3 text-gray-600"
                  @change="updateNewSectionTranslationResolution(conflict.section_name, conflict.language_code, 'ignore')" />
                <div>
                  <span class="text-sm font-medium">Ignorer cette traduction</span>
                  <div class="text-xs text-gray-600 mt-1">Ne pas importer cette traduction</div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sections similaires détectées avec choix d'alignement -->
    <div v-if="similarSections && similarSections.length > 0"
      class="bg-purple-50 border border-purple-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-purple-800">🔗 Sections similaires détectées</h4>
      <div class="space-y-4">
        <div v-for="item in similarSections" :key="item.imported.id" class="bg-white p-4 rounded border">
          <div class="mb-3">
            <div class="flex justify-between items-start">
              <span class="font-medium text-gray-900">{{ item.imported?.id || 'N/A' }}</span>
              <span class="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                {{ Math.round((item.similarity || 0) * 100) }}% similaire
              </span>
            </div>
            <div class="text-sm text-gray-600 mt-1">
              Similaire à la section existante: <strong>"{{ item.existing?.name || 'N/A' }}"</strong>
            </div>
          </div>

          <div class="space-y-2">
            <span class="font-medium text-gray-700 text-sm">Que faire avec cette section ?</span>
            <div class="flex flex-col space-y-2">
              <label
                class="flex items-center cursor-pointer p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                <input type="radio" :name="`similar_${item.imported.id}`" value="create_new"
                  :checked="(similarSectionChoices[item.imported.id]?.action || 'create_new') === 'create_new'"
                  class="mr-3 text-blue-600" @change="updateSimilarSectionChoice(item.imported.id, 'create_new')" />
                <div>
                  <span class="text-sm font-medium">Créer comme nouvelle section</span>
                  <div class="text-xs text-gray-600 mt-1">Les deux sections coexisteront</div>
                </div>
              </label>

              <label
                class="flex items-center cursor-pointer p-2 bg-green-50 rounded hover:bg-green-100 transition-colors">
                <input type="radio" :name="`similar_${item.imported.id}`" value="align_with_existing"
                  :checked="(similarSectionChoices[item.imported.id]?.action || 'create_new') === 'align_with_existing'"
                  class="mr-3 text-green-600"
                  @change="updateSimilarSectionChoice(item.imported.id, 'align_with_existing', item.existing?.name)" />
                <div>
                  <span class="text-sm font-medium">Aligner avec la section existante</span>
                  <div class="text-xs text-gray-600 mt-1">Fusionner avec "{{ item.existing?.name || 'N/A' }}"</div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Nouvelles sections avec détection de similarité améliorée -->
    <div v-if="newSectionsWithSimilarity && newSectionsWithSimilarity.length > 0"
      class="bg-orange-50 border border-orange-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-orange-800">🔍 Nouvelles sections avec sections similaires détectées</h4>
      <div class="space-y-4">
        <div v-for="section in newSectionsWithSimilarity" :key="section.imported.id"
          class="bg-white p-4 rounded border">
          <div class="mb-3">
            <div class="flex justify-between items-start">
              <span class="font-medium text-gray-900">Nouvelle section: {{ section.imported.id }}</span>
              <span class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                Similarité détectée
              </span>
            </div>
          </div>

          <div class="mb-4">
            <span class="font-medium text-gray-700 text-sm">Sections existantes similaires :</span>
            <div class="mt-2 space-y-2">
              <div v-for="similar in section.similar" :key="similar.section.name"
                class="flex items-center justify-between bg-gray-50 p-2 rounded">
                <span class="text-sm">{{ similar.section.name }}</span>
                <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                  {{ Math.round(similar.similarity * 100) }}% similaire
                </span>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <span class="font-medium text-gray-700 text-sm">Action :</span>
            <div class="flex space-x-2">
              <label
                class="flex items-center cursor-pointer bg-green-50 rounded px-3 py-2 hover:bg-green-100 transition-colors">
                <input type="radio" :name="`new_similar_${section.imported.id}`" value="create_new"
                  :checked="(similarSectionChoices[section.imported.id]?.action || 'create_new') === 'create_new'"
                  class="mr-2 text-green-600" @change="updateSimilarSectionChoice(section.imported.id, 'create_new')" />
                <span class="text-sm font-medium">Créer nouvelle</span>
              </label>

              <label
                class="flex items-center cursor-pointer bg-orange-50 rounded px-3 py-2 hover:bg-orange-100 transition-colors">
                <input type="radio" :name="`new_similar_${section.imported.id}`" value="align_with_existing"
                  :checked="(similarSectionChoices[section.imported.id]?.action || 'create_new') === 'align_with_existing'"
                  class="mr-2 text-orange-600"
                  @change="showSimilarSectionSelector(section.imported.id, section.similar)" />
                <span class="text-sm font-medium">Aligner avec existante</span>
              </label>
            </div>

            <!-- Sélecteur de section cible quand "Aligner avec existante" est sélectionné -->
            <div v-if="(similarSectionChoices[section.imported.id]?.action || 'create_new') === 'align_with_existing'"
              class="mt-3">
              <label class="block text-sm font-medium text-gray-700 mb-2">Section cible :</label>
              <select :value="similarSectionChoices[section.imported.id]?.target_section || ''"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                @change="updateSimilarSectionChoice(section.imported.id, 'align_with_existing', ($event.target as HTMLSelectElement).value)">
                <option value="">✅ Sélectionnez une section existante</option>
                <option v-for="similar in section.similar" :key="similar.section.name" :value="similar.section.name">
                  {{ similar.section.name }} ({{ Math.round(similar.similarity * 100) }}% similaire)
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Nouvelles sections -->
    <div v-if="newSections && newSections.length > 0" class="bg-green-50 border border-green-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-green-800">✨ Nouvelles sections à créer</h4>
      <div class="space-y-2">
        <div v-for="section in newSections" :key="section.id"
          class="flex items-center justify-between bg-white p-3 rounded border hover:bg-gray-50 transition-colors">
          <div class="flex items-center">
            <input type="checkbox" :checked="(newSectionActions[section.id] || 'create') === 'create'"
              class="mr-3 text-green-600"
              @change="updateNewSectionAction(section.id, ($event.target as HTMLInputElement).checked ? 'create' : 'skip')" />
            <div>
              <span class="font-medium text-gray-900">{{ section.id }}</span>
              <span v-if="section.type" class="text-sm text-gray-600 ml-2">({{ section.type }})</span>
            </div>
          </div>
          <span v-if="section.translations" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {{ Object.keys(section.translations).length }} traduction(s)
          </span>
        </div>
      </div>
    </div>

    <!-- Sections supprimées lors de mise à jour -->
    <div v-if="removedSections && removedSections.length > 0" class="bg-red-50 border border-red-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-red-800">🗑️ Sections qui seront supprimées du schéma</h4>
      <div class="mb-3 text-sm text-red-700">
        Ces sections étaient dans l'ancien schéma mais ne sont plus dans la nouvelle version. Par défaut, elles seront
        supprimées.
      </div>
      <div class="space-y-2">
        <div v-for="section in removedSections" :key="section.id"
          class="flex items-center justify-between bg-white p-3 rounded border hover:bg-gray-50 transition-colors">
          <div class="flex items-center">
            <input type="checkbox" :checked="keepRemovedSections.has(section.id)" class="mr-3 text-yellow-600"
              @change="updateRemovedSectionAction(section.id, ($event.target as HTMLInputElement).checked ? 'keep' : 'remove')" />
            <div>
              <span class="font-medium text-gray-900">{{ section.name }}</span>
              <span v-if="section.type" class="text-sm text-gray-600 ml-2">({{ section.type }})</span>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <span v-if="section.has_translations" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Avec traductions
            </span>
            <span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
              Sera supprimée
            </span>
          </div>
        </div>
      </div>
      <div class="mt-3 text-xs text-red-600">
        💡 Cochez les sections que vous voulez conserver malgré leur absence dans la nouvelle version.
      </div>
    </div>

    <!-- Aperçu final -->
    <div class="bg-gray-50 border border-gray-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-gray-800">👁️ Aperçu de l'import</h4>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span class="font-medium text-gray-700">Sections à créer:</span>
          <span class="ml-2 font-bold text-green-600">{{ sectionsToCreate }}</span>
        </div>
        <div>
          <span class="font-medium text-gray-700">Sections à assigner:</span>
          <span class="ml-2 font-bold text-blue-600">{{ sectionsToUpdate }}</span>
        </div>
        <div v-if="removedSections.length > 0">
          <span class="font-medium text-gray-700">Sections à supprimer:</span>
          <span class="ml-2 font-bold text-red-600">{{ sectionsToRemove }}</span>
        </div>
        <div v-if="sectionsToKeep > 0">
          <span class="font-medium text-gray-700">Sections conservées:</span>
          <span class="ml-2 font-bold text-yellow-600">{{ sectionsToKeep }}</span>
        </div>
        <div>
          <span class="font-medium text-gray-700">Traductions à traiter:</span>
          <span class="ml-2 font-bold text-purple-600">{{ translationsToProcess }}</span>
        </div>
        <div>
          <span class="font-medium text-gray-700">Conflits à résoudre:</span>
          <span class="ml-2 font-bold text-orange-600">{{ conflictsToResolve }}</span>
        </div>
      </div>
    </div>

    <!-- Vue d'ensemble des sections qui seront assignées au schéma -->
    <div class="bg-blue-50 border border-blue-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-blue-900">📋 Sections à importer</h4>

      <!-- Sections parentes automatiquement ajoutées -->
      <div v-if="parentSections.length > 0" class="mb-4 bg-blue-100 p-3 rounded">
        <h5 class="font-medium text-blue-800 mb-2">📁 Sections parentes (ajoutées automatiquement)</h5>
        <p class="text-sm text-blue-700 mb-2">Ces sections parentes seront automatiquement incluses pour maintenir la
          hiérarchie :</p>
        <div class="flex flex-wrap gap-2">
          <span v-for="parent in parentSections" :key="parent"
            class="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
            {{ parent }} <span class="text-blue-600">(parent requis)</span>
          </span>
        </div>
      </div>

      <!-- Tableau des sections -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-3 py-2 text-left font-medium text-gray-700">IMPORT</th>
              <th class="px-3 py-2 text-left font-medium text-gray-700">SECTION</th>
              <th class="px-3 py-2 text-left font-medium text-gray-700">TYPE</th>
              <th class="px-3 py-2 text-left font-medium text-gray-700">STATUT</th>
              <th class="px-3 py-2 text-left font-medium text-gray-700">ACTION</th>
              <th class="px-3 py-2 text-center font-medium text-gray-700">TRAD</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <!-- Sections importées -->
            <tr v-for="section in allImportSections" :key="section.id" class="hover:bg-gray-50">
              <td class="px-3 py-2">
                <input type="checkbox" :checked="section.will_import" :disabled="section.is_parent_required"
                  class="text-blue-600"
                  @change="handleSectionToggle(section.id, ($event.target as HTMLInputElement).checked)" />
              </td>
              <td class="px-3 py-2">
                <span class="font-medium">{{ section.name }}</span>
              </td>
              <td class="px-3 py-2">
                <span class="text-xs px-2 py-1 rounded" :class="getTypeClass(section.type)">
                  {{ section.type || 'container' }}
                </span>
              </td>
              <td class="px-3 py-2">
                <span class="text-xs px-2 py-1 rounded" :class="getStatusClass(section.status)">
                  {{ getStatusLabel(section.status) }}
                </span>
              </td>
              <td class="px-3 py-2">
                <span class="text-sm" :class="getActionClass(section.action)">
                  {{ getActionLabel(section.action) }}
                </span>
              </td>
              <td class="px-3 py-2 text-center">
                <div class="flex items-center justify-center space-x-1">
                  <!-- Drapeaux des langues -->
                  <span v-for="lang in getSectionTranslationLanguages(section.id)" :key="lang" class="text-sm">
                    {{ getLanguageFlag(lang) }}
                  </span>
                  <!-- Icône d'action avec tooltip -->
                  <div v-if="getSectionTranslationAction(section.id)" class="relative group">
                    <span class="text-sm cursor-help"
                      :class="getTranslationActionIconClass(getSectionTranslationAction(section.id)!)">
                      {{ getTranslationActionIcon(getSectionTranslationAction(section.id)!) }}
                    </span>
                    <div
                      class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      {{ getTranslationActionTooltip(getSectionTranslationAction(section.id)!) }}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Avertissements -->
    <div v-if="warnings.length > 0" class="bg-red-50 border border-red-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-red-800">🚨 Avertissements</h4>
      <ul class="space-y-1">
        <li v-for="warning in warnings" :key="warning" class="text-sm text-red-700 flex items-start">
          <span class="text-red-500 mr-2">•</span>
          {{ warning }}
        </li>
      </ul>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ParsedSchemaWithAnalysis } from '../../../composables/useSchemaImport';
import { SchemaImportUtils } from '../../../utils/schemaImportUtils';

// Props
const props = defineProps<{
  parsedSchema: ParsedSchemaWithAnalysis | null;
  resolutions: Record<string, string>;
  translationResolutions: Record<string, string>;
  newSectionTranslationResolutions: Record<string, string>;
  similarSectionChoices: Record<string, {
    action: 'create_new' | 'align_with_existing';
    target_section?: string;
  }>;
  newSectionActions: Record<string, 'create' | 'skip'>;
  keepRemovedSections: Set<number>;
  schemaConflict?: {
    type: 'uuid_match' | 'name_match' | 'similar_schema' | 'none';
    action?: 'update' | 'create_new' | 'align_existing';
  } | null;
}>();

// Emits
const emit = defineEmits<{
  'update-resolution': [conflictId: string, resolution: string];
  'update-translation-resolution': [sectionName: string, languageCode: string, fieldType: string, resolution: string];
  'update-new-section-translation-resolution': [sectionName: string, languageCode: string, resolution: string];
  'update-similar-section-choice': [sectionId: string, action: 'create_new' | 'align_with_existing', targetSection?: string];
  'update-new-section-action': [sectionId: string, action: 'create' | 'skip'];
  'update-removed-section-action': [sectionId: number, action: 'keep' | 'remove'];
}>();

// Données calculées à partir du schéma analysé
const analysisReport = computed(() => props.parsedSchema?._analysisReport);
const detailedConflicts = computed(() => props.parsedSchema?._detailedConflicts || []);

const translationConflicts = computed(() => {
  const conflicts = analysisReport.value?.translation_conflicts as Array<{
    section_name: string;
    language_code: string;
    field_type?: string;
    existing_translation?: {
      source?: 'global' | 'schema';
      titre: string;
      description: string;
      id?: number;
    };
    imported_translation?: { titre: string; description: string };
    resolution: string;
  }> || [];
  return conflicts;
});

const newSectionTranslationConflicts = computed(() => {
  return analysisReport.value?.new_section_translation_conflicts as Array<{
    section_name: string;
    language_code: string;
    imported_translation?: { titre?: string; description?: string };
  }> || [];
});

const similarSections = computed(() => {
  const sections = analysisReport.value?.sections.similar_sections as Array<{
    imported: { id: string };
    existing: { name: string };
    similarity: number;
  }> || [];

  // Filtrer les sections avec des données valides
  return sections.filter(section =>
    section.imported?.id &&
    section.existing?.name &&
    typeof section.similarity === 'number'
  );
});

const newSections = computed(() => {
  const sections = analysisReport.value?.sections.new_sections as Array<{
    id: string;
    type?: string;
    translations?: Record<string, unknown>;
  }> || [];

  // Filtrer les sections qui sont alignées avec des sections existantes
  return sections.filter(section => {
    const similarChoice = props.similarSectionChoices[section.id];
    return !(similarChoice && similarChoice.action === 'align_with_existing');
  });
});

const removedSections = computed(() => {
  // Ne montrer les sections supprimées que si on met à jour un schéma existant
  if (!props.parsedSchema || !props.parsedSchema._analysisReport) return [];

  // Vérifier qu'on est en mode mise à jour (pas nouveau schéma)
  const isUpdatingExistingSchema = props.schemaConflict &&
    (props.schemaConflict.type === 'uuid_match' ||
      props.schemaConflict.action === 'update');

  if (!isUpdatingExistingSchema) {
    return []; // Pas de sections supprimées en mode création
  }

  const sections = props.parsedSchema._analysisReport.removed_sections as Array<{
    id: number;
    name: string;
    type?: string;
    has_translations: boolean;
    usage_count?: number;
  }> || [];

  // Obtenir les sections qui seront alignées avec des sections existantes
  const alignedSections = new Set<string>();
  Object.values(props.similarSectionChoices).forEach(choice => {
    if (choice.action === 'align_with_existing' && choice.target_section) {
      alignedSections.add(choice.target_section);
    }
  });

  // Filtrer pour ne montrer que les sections "finales" (pas les parents automatiques)
  // et exclure les sections qui seront réutilisées via alignement
  return sections.filter(section => {
    // Exclure si cette section sera réutilisée via alignement
    if (alignedSections.has(section.name)) {
      return false;
    }

    // Une section finale est une section qui n'a pas d'enfants dans la liste des sections supprimées
    const hasChildrenInRemoved = sections.some(otherSection =>
      otherSection.name.startsWith(section.name + '.') && otherSection.name !== section.name
    );
    return !hasChildrenInRemoved;
  });
});

const newSectionsWithSimilarity = computed(() => {
  const sections = analysisReport.value?.similar_sections_detected as Array<{
    imported: { id: string };
    similar_candidates: Array<{
      section: { id: number; name: string; type?: string };
      similarity: number;
      reason: string;
    }>;
  }> || [];

  // Transformer les données au format attendu par le template
  return sections.map(section => ({
    imported: section.imported,
    similar: section.similar_candidates.map(candidate => ({
      section: candidate.section,
      similarity: candidate.similarity,
      reason: candidate.reason
    }))
  }));
});

// Compteurs pour l'aperçu
const sectionsToCreate = computed((): number => {
  return newSections.value.filter(section =>
    (props.newSectionActions[section.id] || 'create') === 'create'
  ).length;
});

const sectionsToUpdate = computed((): number => {
  // Compter les sections existantes
  const existingSections = analysisReport.value?.summary.existing_sections || 0;

  // Compter les sections qui seront alignées avec des sections existantes
  const alignedSections = Object.values(props.similarSectionChoices).filter(choice =>
    choice.action === 'align_with_existing'
  ).length;

  return existingSections + alignedSections;
});

const translationsToProcess = computed((): number => {
  let count = 0;
  if (props.parsedSchema) {
    for (const section of props.parsedSchema.section_list) {
      if (section.translations) {
        count += Object.keys(section.translations).length;
      }
    }
  }
  return count;
});

const conflictsToResolve = computed((): number => {
  return detailedConflicts.value.length + translationConflicts.value.length;
});

const sectionsToRemove = computed((): number => {
  return removedSections.value.filter(section =>
    !props.keepRemovedSections.has(section.id)
  ).length;
});

const sectionsToKeep = computed((): number => {
  return removedSections.value.filter(section =>
    props.keepRemovedSections.has(section.id)
  ).length;
});

// Computed properties pour le tableau récapitulatif
const parentSections = computed((): string[] => {
  const parents = new Set<string>();

  if (props.parsedSchema) {
    for (const section of props.parsedSchema.section_list) {
      const action = props.newSectionActions[section.id] || 'create';
      if (action !== 'skip') {
        let currentName = section.id;
        while (currentName.includes('.')) {
          const parentName = currentName.substring(0, currentName.lastIndexOf('.'));
          if (parentName && !props.parsedSchema.section_list.some(s => s.id === parentName)) {
            parents.add(parentName);
          }
          currentName = parentName;
        }
      }
    }
  }

  return Array.from(parents).sort();
});

const allImportSections = computed(() => {
  const sections: Array<{
    id: string;
    name: string;
    type?: string;
    status: 'new' | 'existing' | 'similar';
    action: string;
    will_import: boolean;
    is_parent_required: boolean;
    conflicts: number;
  }> = [];

  if (!props.parsedSchema) return sections;

  for (const section of props.parsedSchema.section_list) {
    const action = props.newSectionActions[section.id] || 'create';
    const similarChoice = props.similarSectionChoices[section.id];

    // Déterminer le statut
    let status: 'new' | 'existing' | 'similar' = 'new';
    if (analysisReport.value?.sections.existing_sections.some((s: any) => s.imported.id === section.id)) {
      status = 'existing';
    } else if (newSectionsWithSimilarity.value.some(s => s.imported.id === section.id)) {
      status = 'similar';
    }

    // Compter les conflits
    let conflicts = 0;
    conflicts += detailedConflicts.value.filter(c => c.section_id === section.id).length;
    conflicts += translationConflicts.value.filter(c => c.section_name === section.id).length;

    let finalAction = '';
    let willImport = true;

    if (status === 'existing') {
      finalAction = 'Assigner';
    } else if (status === 'similar' && similarChoice?.action === 'align_with_existing') {
      finalAction = `Aligner avec ${similarChoice.target_section || 'existante'}`;
    } else if (action === 'skip') {
      finalAction = 'Ignorer';
      willImport = false;
    } else {
      finalAction = 'Créer nouvelle';
    }

    sections.push({
      id: section.id,
      name: section.id,
      type: section.type,
      status,
      action: finalAction,
      will_import: willImport,
      is_parent_required: parentSections.value.includes(section.id),
      conflicts
    });
  }

  return sections.sort((a, b) => a.name.localeCompare(b.name));
});

// Avertissements
const warnings = computed((): string[] => {
  const warns: string[] = [];

  // Vérifier les champs sans section finale
  const fieldsWithoutFinalSection = detectFieldsWithoutFinalSection();
  if (fieldsWithoutFinalSection.length > 0) {
    warns.push(`${fieldsWithoutFinalSection.length} champ(s) du schéma existant ne correspondent à aucune section finale : ${fieldsWithoutFinalSection.join(', ')}`);
  }

  // Vérifier les sections ignorées
  const skippedSections = newSections.value.filter(section =>
    props.newSectionActions[section.id] === 'skip'
  ).length;

  if (skippedSections > 0) {
    warns.push(`${skippedSections} section(s) ne seront pas créées`);
  }

  // Vérifier les sections supprimées  
  if (sectionsToRemove.value > 0) {
    warns.push(`${sectionsToRemove.value} section(s) seront supprimées du schéma - données potentiellement perdues`);
  }

  // Vérifier les sections conservées
  if (sectionsToKeep.value > 0) {
    warns.push(`${sectionsToKeep.value} section(s) conservées malgré leur absence dans la nouvelle version`);
  }

  // Vérifier les alignements problématiques
  const alignments = Object.values(props.similarSectionChoices).filter(choice =>
    choice.action === 'align_with_existing'
  ).length;

  if (alignments > 0) {
    warns.push(`${alignments} section(s) seront alignées avec des sections existantes - vérifiez la cohérence`);
  }

  // Vérifier les écrasements de traductions globales
  const globalOverwrites = Object.values(props.translationResolutions).filter(resolution =>
    resolution === 'overwrite_global'
  ).length;

  if (globalOverwrites > 0) {
    warns.push(`${globalOverwrites} traduction(s) globales seront écrasées - cela affectera tous les schémas`);
  }

  return warns;
});

// Gestionnaires d'événements
const updateResolution = (conflictId: string, resolution: string): void => {
  emit('update-resolution', conflictId, resolution);
};

const updateTranslationResolution = (sectionName: string, languageCode: string, fieldType: string, resolution: string): void => {
  emit('update-translation-resolution', sectionName, languageCode, fieldType, resolution);
};

const updateSimilarSectionChoice = (sectionId: string, action: 'create_new' | 'align_with_existing', targetSection?: string): void => {
  emit('update-similar-section-choice', sectionId, action, targetSection);

  // Les sections alignées sont automatiquement filtrées de la liste "Nouvelles sections à créer"
  // pas besoin de les modifier dans newSectionActions
};

const updateNewSectionAction = (sectionId: string, action: 'create' | 'skip'): void => {
  emit('update-new-section-action', sectionId, action);
};

const updateRemovedSectionAction = (sectionId: number, action: 'keep' | 'remove'): void => {
  emit('update-removed-section-action', sectionId, action);
};

// Utilitaires
const getConflictTypeLabel = (type: string): string => {
  return SchemaImportUtils.getConflictTypeLabel(type);
};

const getTranslationResolution = (conflict: { section_name: string; language_code: string; field_type?: string }): string => {
  const key = `${conflict.section_name}_${conflict.language_code}_${conflict.field_type || 'all'}`;
  return props.translationResolutions[key] || 'create_schema_specific'; // Défaut à schéma-spécifique
};

const getNewSectionTranslationResolution = (conflict: { section_name: string; language_code: string }): string => {
  // Pour les nouvelles sections, on utilise une clé simplifiée sans field_type
  const key = `${conflict.section_name}_${conflict.language_code}_all`;
  return props.newSectionTranslationResolutions?.[key] || 'create_schema_specific';
};

const updateNewSectionTranslationResolution = (sectionName: string, languageCode: string, resolution: string): void => {
  emit('update-new-section-translation-resolution', sectionName, languageCode, resolution);
};

const showSimilarSectionSelector = (sectionId: string, similarSections: Array<{ section: { name: string }; similarity: number }>): void => {
  // Marquer automatiquement cette section comme "align_with_existing" et pré-sélectionner la section la plus similaire
  const bestMatch = similarSections[0]; // Le plus similaire (déjà trié par similarité)
  if (bestMatch) {
    updateSimilarSectionChoice(sectionId, 'align_with_existing', bestMatch.section.name);
  } else {
    updateSimilarSectionChoice(sectionId, 'align_with_existing');
  }
};

// Fonctions utilitaires pour le tableau
const handleSectionToggle = (sectionId: string, checked: boolean): void => {
  updateNewSectionAction(sectionId, checked ? 'create' : 'skip');
};

const getTypeClass = (type?: string): string => {
  if (!type || type === 'container') return 'bg-gray-100 text-gray-800';
  if (type === 'text') return 'bg-blue-100 text-blue-800';
  if (type === 'number') return 'bg-green-100 text-green-800';
  if (type === 'date') return 'bg-purple-100 text-purple-800';
  if (type === 'boolean') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
};

const getStatusClass = (status: 'new' | 'existing' | 'similar'): string => {
  if (status === 'existing') return 'bg-green-100 text-green-800';
  if (status === 'similar') return 'bg-orange-100 text-orange-800';
  return 'bg-blue-100 text-blue-800'; // new
};

const getStatusLabel = (status: 'new' | 'existing' | 'similar'): string => {
  if (status === 'existing') return '✅ Existante';
  if (status === 'similar') return '🔗 Similaire';
  return '🆕 Nouvelle';
};

const getActionClass = (action: string): string => {
  if (action === 'Ignorer') return 'text-gray-500';
  if (action === 'Assigner') return 'text-blue-600';
  if (action.startsWith('Aligner')) return 'text-orange-600';
  return 'text-green-600'; // Créer nouvelle
};

const getActionLabel = (action: string): string => {
  return action;
};

// Fonctions pour la colonne TRAD
const getSectionTranslationLanguages = (sectionId: string): string[] => {
  if (!props.parsedSchema) return [];

  const section = props.parsedSchema.section_list.find(s => s.id === sectionId);
  if (!section?.translations) return [];

  return Object.keys(section.translations);
};

const getLanguageFlag = (languageCode: string): string => {
  const flags: Record<string, string> = {
    'fr': '🇫🇷',
    'en': '🇬🇧',
    'es': '🇪🇸',
    'de': '🇩🇪',
    'it': '🇮🇹',
    'pt': '🇵🇹',
    'nl': '🇳🇱',
    'pl': '🇵🇱',
    'ru': '🇷🇺',
    'ja': '🇯🇵',
    'ko': '🇰🇷',
    'zh': '🇨🇳',
    'ar': '🇸🇦'
  };
  return flags[languageCode] || '🌐';
};

const getSectionTranslationAction = (sectionId: string): string | null => {
  // Rechercher s'il y a des conflits de traduction pour cette section
  const conflicts = translationConflicts.value.filter(c => c.section_name === sectionId);
  if (conflicts.length === 0) return null;

  // Prendre la résolution du premier conflit (ils devraient tous avoir la même résolution)
  const resolution = getTranslationResolution(conflicts[0]);
  return resolution;
};

const getTranslationActionIcon = (action: string): string => {
  const icons: Record<string, string> = {
    'create_schema_specific': '📝',
    'update_schema_translation': '🔄',
    'use_global': '📄',
    'overwrite_global': '⚠️',
    'keep_existing': '📋'
  };
  return icons[action] || '❓';
};

const getTranslationActionIconClass = (action: string): string => {
  const classes: Record<string, string> = {
    'create_schema_specific': 'text-blue-600',
    'update_schema_translation': 'text-green-600',
    'use_global': 'text-gray-600',
    'overwrite_global': 'text-red-600',
    'keep_existing': 'text-yellow-600'
  };
  return classes[action] || 'text-gray-400';
};

const getTranslationActionTooltip = (action: string): string => {
  const tooltips: Record<string, string> = {
    'create_schema_specific': 'Créer traduction schéma-spécifique',
    'update_schema_translation': 'Mettre à jour traduction du schéma',
    'use_global': 'Utiliser traduction globale',
    'overwrite_global': 'Écraser traduction globale',
    'keep_existing': 'Conserver traduction existante'
  };
  return tooltips[action] || 'Action inconnue';
};

// Fonction pour détecter les champs sans section finale
const detectFieldsWithoutFinalSection = (): string[] => {
  const fieldsWithoutSection: string[] = [];

  if (!props.parsedSchema || !analysisReport.value) return fieldsWithoutSection;

  // Récupérer les sections qui seront présentes après l'import
  const finalSections = new Set<string>();

  // Ajouter les sections existantes qui ne seront pas supprimées
  if (analysisReport.value.removed_sections) {
    const alignedSections = new Set<string>();
    Object.values(props.similarSectionChoices).forEach(choice => {
      if (choice.action === 'align_with_existing' && choice.target_section) {
        alignedSections.add(choice.target_section);
      }
    });

    const removedSectionNames = new Set(
      (analysisReport.value.removed_sections as Array<{ name: string }>)
        .filter(section => !alignedSections.has(section.name))
        .filter(section => !props.keepRemovedSections.has((section as any).id))
        .map(section => section.name)
    );

    // Ajouter toutes les sections existantes qui ne seront pas supprimées
    if (analysisReport.value.sections?.existing_sections) {
      (analysisReport.value.sections.existing_sections as Array<{ existing: { name: string } }>)
        .forEach(item => {
          if (!removedSectionNames.has(item.existing.name)) {
            finalSections.add(item.existing.name);
          }
        });
    }
  }

  // Ajouter les nouvelles sections qui seront créées
  if (props.parsedSchema.section_list) {
    props.parsedSchema.section_list.forEach(section => {
      const action = props.newSectionActions[section.id] || 'create';
      const similarChoice = props.similarSectionChoices[section.id];

      if (action === 'create' && !(similarChoice?.action === 'align_with_existing')) {
        finalSections.add(section.id);
      } else if (similarChoice?.action === 'align_with_existing' && similarChoice.target_section) {
        finalSections.add(similarChoice.target_section);
      }
    });
  }

  // TODO: Récupérer les vrais champs du schéma existant depuis l'analyse
  // const schemaFields = analysisReport.value?.existing_schema_fields || [];

  // Pour l'instant, pas de champs fictifs - sera implémenté quand les données seront disponibles
  // schemaFields.forEach(fieldPath => {
  //   // Vérifier si ce champ correspond à une section finale
  //   let hasMatchingSection = false;
  //   
  //   for (const sectionName of finalSections) {
  //     if (fieldPath.startsWith(sectionName)) {
  //       hasMatchingSection = true;
  //       break;
  //     }
  //   }
  //   
  //   if (!hasMatchingSection) {
  //     fieldsWithoutSection.push(fieldPath);
  //   }
  // });

  return fieldsWithoutSection;
};
</script>